import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './sidebar';
import OrderDetailsModal from './modal_windows/OrderDetailsModal';

import {
  StatCard, RefreshButton, SearchField,
  StatusBadge, TableCard, LoadingState, EmptyState, Pagination,
} from '../../ui/CrmPage';

const PAGE_SIZE = 15;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    timeZone: 'Europe/Moscow',
  });
};

const LostOrders = () => {
  const [orders, setOrders] = useState([]);
  const [orderDetails, setOrderDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}v1/lostorder_view`)
      .then(response => response.json())
      .then(data => {
        if (Array.isArray(data)) {
          setOrders(data);
          data.forEach(order => fetchOrderDetails(order.id_orders));
        }
      })
      .catch(error => console.error('Ошибка:', error))
      .finally(() => setLoading(false));
  }, [reloadKey]);

  const fetchOrderDetails = (orderId) => {
    fetch(`${process.env.REACT_APP_API_URL}v1/order_orderitems/${orderId}`)
      .then(response => response.json())
      .then(data => setOrderDetails(prev => ({ ...prev, [orderId]: data })))
      .catch(error => console.error('Ошибка:', error));
  };

  const handleOrderClick = (orderId) => {
    const order = orders.find(o => o.id_orders === orderId);
    const details = orderDetails[orderId];
    if (order) {
      const items = Array.isArray(details)
        ? details.filter(item => Number(item['Количество'] || item.quantity) > 0)
        : [];
      setSelectedOrderDetails({ ...order, id: order.id_orders, items });
      setIsModalOpen(true);
    }
  };

  const summary = useMemo(() => ({
    total: orders.length,
    cancelled: orders.filter(o => /отмен/i.test(o.status_name || '')).length,
    returned:  orders.filter(o => /возврат/i.test(o.status_name || '')).length,
    rejected:  orders.filter(o => /отклон/i.test(o.status_name || '')).length,
  }), [orders]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return orders.filter(o => {
      if (!term) return true;
      return (
        (o.client_name || '').toLowerCase().includes(term) ||
        (o.delivery_address || '').toLowerCase().includes(term) ||
        String(o.order_number || '').includes(term)
      );
    });
  }, [orders, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paginatedOrders = filteredOrders.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <div className="lost-orders-container crmpage">
      <div className="lost-orders-sidebar">
        <Sidebar />
      </div>

      <div className="lost-orders-basic-container crmpage__main">
        <header className="crmpage__head">
          <h1 className="crmpage__title">Невыполненные заказы</h1>
        </header>

        <section className="crmpage__stats">
          <StatCard label="Всего"      value={summary.total}     accent="neutral" />
          <StatCard label="Отменены"   value={summary.cancelled} accent="danger" />
          <StatCard label="Возвраты"   value={summary.returned}  accent="warning" />
          <StatCard label="Отклонены"  value={summary.rejected}  accent="info" />
          <RefreshButton onClick={() => setReloadKey(k => k + 1)} />
        </section>

        <section className="crmpage__filters">
          <SearchField
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Поиск: номер, клиент, адрес…"
          />
        </section>

        <TableCard>
          {loading ? (
            <LoadingState text="Загружаем невыполненные…" />
          ) : paginatedOrders.length === 0 ? (
            <EmptyState title="Пусто" text="Здесь будут невыполненные / отменённые заказы." />
          ) : (
            <>
              <div className="crmpage__table-wrap">
                <table className="crmpage__table">
                  <colgroup>
                    <col style={{ width: 80 }} />
                    <col />
                    <col />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 100 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Номер</th>
                      <th>Клиент</th>
                      <th className="crmpage__hide-880">Адрес доставки</th>
                      <th>Причина</th>
                      <th className="crmpage__hide-1000">Дата</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedOrders.map((order, idx) => (
                      <tr key={idx} className="crmpage__row" onClick={() => handleOrderClick(order.id_orders)}>
                        <td><span className="crmpage__num">№{order.order_number}</span></td>
                        <td>
                          <div className="crmpage__primary">{order.client_name || '—'}</div>
                          <div className="crmpage__sub">{order.region_name}</div>
                        </td>
                        <td className="crmpage__muted crmpage__hide-880">{order.delivery_address || '—'}</td>
                        <td><StatusBadge name={order.status_name} /></td>
                        <td className="crmpage__date crmpage__hide-1000">{formatDate(order.delivery_date || order.order_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="crmpage__cards">
                {paginatedOrders.map((o, idx) => (
                  <div key={idx} className="crmpage__card" onClick={() => handleOrderClick(o.id_orders)}>
                    <div className="crmpage__card-head">
                      <span className="crmpage__num">№{o.order_number}</span>
                      <StatusBadge name={o.status_name} />
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Клиент</span>
                      <span className="crmpage__card-val">{o.client_name || '—'}</span>
                    </div>
                    <div className="crmpage__card-row">
                      <span className="crmpage__card-label">Адрес</span>
                      <span className="crmpage__card-val">{o.delivery_address || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>

              <Pagination current={currentPage} total={totalPages} onChange={setCurrentPage} />
            </>
          )}
        </TableCard>
      </div>

      {isModalOpen && selectedOrderDetails && (
        <OrderDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          order={selectedOrderDetails}
        />
      )}
    </div>
  );
};

export default LostOrders;
